import { httpClient } from '@/core/api/httpClient';
import { roleFromApi } from '@/core/constants/roles';
import type {
  Organization,
  OrganizationDetail,
  CreateOrganizationPayload,
  UpdateOrganizationPayload,
} from '../types';
import { organizationsMock } from './organizations.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

export const organizationsService = {
  getAll: async (signal?: AbortSignal): Promise<Organization[]> => {
    if (USE_MOCK) return organizationsMock;
    const { data } = await httpClient.get<Array<Omit<Organization, 'role'> & { role: string }>>(
      `${API_V1}/me/tenants`,
      { signal },
    );
    return data.map((org) => ({ ...org, role: roleFromApi(org.role) }));
  },

  getById: async (id: string, signal?: AbortSignal): Promise<OrganizationDetail> => {
    if (USE_MOCK) {
      const mock = organizationsMock.find((o) => o.id === id);
      return {
        id: mock?.id ?? id,
        name: mock?.name ?? '',
        slug: mock?.id ?? id,
        tier: mock?.tier ?? 'free',
        memberLimit: 5,
        avatarUrl: mock?.avatarUrl ?? null,
        description: mock?.description ?? null,
        websiteUrl: mock?.websiteUrl ?? null,
        color: 'indigo-deep',
        ownerId: mock?.ownerId ?? '',
        membersCount: 1,
        createdAt: mock?.createdAt ?? new Date().toISOString(),
      };
    }
    const { data } = await httpClient.get<OrganizationDetail>(`${API_V1}/tenants`, { signal });
    return data;
  },

  create: async (payload: CreateOrganizationPayload): Promise<OrganizationDetail> => {
    if (USE_MOCK) {
      return {
        id: `org-${Date.now()}`,
        name: payload.name,
        slug: payload.slug,
        tier: 'free',
        memberLimit: 5,
        avatarUrl: null,
        description: null,
        websiteUrl: null,
        color: payload.color,
        ownerId: '',
        membersCount: 1,
        createdAt: new Date().toISOString(),
      };
    }
    const { data } = await httpClient.post<CreateOrganizationPayload, OrganizationDetail>(
      `${API_V1}/tenants`,
      payload,
    );
    return data;
  },

  update: async (_id: string, payload: UpdateOrganizationPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<UpdateOrganizationPayload, void>(`${API_V1}/tenants`, payload);
  },

  delete: async (_id: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${API_V1}/tenants`);
  },

  uploadAvatar: (tenantId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.patch<FormData, void>(`${API_V1}/tenants/${tenantId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAvatar: (tenantId: string) =>
    httpClient.delete<void>(`${API_V1}/tenants/${tenantId}/avatar`),
};
